import { Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import {
  AddAssemblyPage,
  BulkReplaceReviewPage,
  DetailAssemblyPage,
  EditAssemblyPage,
} from 'src/features/assemblies';
import {
  AddComponentPage,
  DetailComponentPage,
  EditComponentPage,
} from 'src/features/components';
import {
  AddLocationGroupPage,
  AddLocationTagPage,
  AssignLocationGroupPage,
  CalendarLocationGroupPage,
  CalendarLocationPage,
  DetailLocationGroupPage,
  DetailLocationPage,
  DetailLocationPageOO,
  EditLocationTagPage,
  ListLocationTagsPage,
  LocationGroupSubNavbar,
  // TODO: Remove LocationListPage once the new page is verified and ready to be used for all users
  // LocationListPageOO,
  LocationListPageOOV2,
  LocationSubNavbar,
  ScheduleLocationGroupPage,
  ScheduleLocationPage,
  ScheduleLocationPageOO,
  TimingLocationGroupPage,
  TimingLocationPage,
  ViewLocationHistoryPage,
} from 'src/features/locations';
import {
  AddProductCategoryPage,
  AddProductPage,
  EditProductCategoryPage,
  EditProductPage,
  ListProductsPage,
  ManageProductCategoryPage,
  ReviewProductPage,
} from 'src/features/products';

import DesignSystemThemeProvider from './components/designSystem/DesignSystemThemeProvider';
import { AssetUploadModal } from './components/modal/assetUploadModal/AssetUploadModal';
import { NotFoundPage } from './components/notFoundPage';
import { PageWrapper } from './components/pageWrapper';
import { PATHS } from './constants/paths';
import { AddAssemblyTagPage } from './features/assemblies/addAssemblyTagPage/AddAssemblyTagPage';
import { EditAssemblyTagPage } from './features/assemblies/editAssemblyTagPage/EditAssemblyTagPage';
import { AddLayoutsPage } from './features/assemblies/layouts/addLayoutsPage/AddLayoutsPage';
import { CreateRotationSectionsLayoutsPage } from './features/assemblies/layouts/createRotationSectionsLayoutsPage/CreateRotationSectionsLayoutsPage';
import { CreateSectionsLayoutsPage } from './features/assemblies/layouts/createSectionsLayoutsPage/CreateSectionsLayoutsPage';
import { EditLayoutsPage } from './features/assemblies/layouts/editLayoutsPage/EditLayoutsPage';
import { ListLayoutsPage } from './features/assemblies/layouts/listLayoutsPage/ListLayoutsPage';
import { ManageAssemblyTagPage } from './features/assemblies/manageAssemblyTagPage/ManageAssemblyTagPage';
import { AddComponentTagPage } from './features/components/addComponentTagPage/AddComponentTagPage';
import { BulkReplaceComponentPage } from './features/components/bulkReplaceComponentPage/BulkReplaceComponentPage';
import { EditComponentTagPage } from './features/components/editComponentTagPage/EditComponentTagPage';
import { ReplaceComponentPage } from './features/components/listComponentsPage/modules/replacementComponent/ReplaceComponentPage';
import { ManageComponentTagPage } from './features/components/manageComponentTagPage/ManageComponentTagPage';
import {
  AssemblyState,
  DevPublications,
  LayoutsPage,
} from './features/devTools';
import { DesignSystemPage } from './features/devTools/designSystem';
import { AssemblyPublicationPage } from './features/locations/listAssemblyPublicationPage/AssemblyPublicationPage';
import { PublicationPage } from './features/locations/listPublicationPage/PublicationPage';
import { ProductPublicationPage } from './features/locations/listPublicationPage/publications/ProductPublicationPage';
import { BulkEditMessagingPage } from './features/locations/locationListPage/bulkEditMessagingPage/BulkEditMessagingPage';
import { LocationListPage } from './features/locations/locationListPage/LocationListPage';
import { LocationGroupTab } from './features/locations/locationListPage/modules/LocationGroupTab/LocationGroupTab';
import { LocationTab } from './features/locations/locationListPage/modules/LocationTab/LocationTab';
import { UnassignedLocationTab } from './features/locations/locationListPage/modules/UnassignedLocationTab/UnassignedLocationTab';
import { MenuBoardModificationPage } from './features/locations/menuBoardModificationPage';
import { ScheduleComponentPage } from './features/locations/scheduleComponentPage';
import { SchedulePreviewPage } from './features/locations/schedulePreviewPage';
import { LoginSelect } from './features/login';
import { AddVariationTemplatePage } from './features/products/addVariationTemplatePage/AddVariationTemplatePage';
import { EditVariationTemplatePage } from './features/products/editVariationTemplatePage/EditVariationTemplatePage';
import {
  AboutPage,
  CustomerTriggeredEventsPage,
  EditAccessibilityPage,
  EditGlobalSettingsPage,
  ListUserActionsPage,
  SettingsSubNavbar,
} from './features/settings';

export const routesByDefault = [
  {
    path: '/login-select',
    element: <LoginSelect />,
  },
  {
    path: '/',
    element: <PageWrapper />,
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <Navigate to={PATHS.ASSEMBLIES} replace />
          </Suspense>
        ),
      },
      {
        path: 'components',
        children: [
          {
            path: '',
            async lazy() {
              let { ListComponentsPage } = await import(
                'src/features/components/listComponentsPage/ListComponentsPage'
              );

              return {
                Component: ListComponentsPage,
              };
            },
          },
          {
            path: 'tags',
            children: [
              { path: 'add', element: <AddComponentTagPage /> },
              {
                path: 'edit/:tagId',
                element: <EditComponentTagPage />,
              },
              {
                path: 'manage',
                element: <ManageComponentTagPage />,
              },
            ],
          },
          { path: 'add', element: <AddComponentPage /> },
          { path: 'edit/:componentId', element: <EditComponentPage /> },
          { path: 'details/:componentId', element: <DetailComponentPage /> },
          {
            path: 'component-replace',
            element: <ReplaceComponentPage />,
          },
          {
            path: 'bulk-replace',
            element: <BulkReplaceComponentPage />,
          },
        ],
      },
      {
        path: 'locations',
        children: [
          {
            path: '',
            element: <LocationListPage />,

            children: [
              { path: '', element: <Navigate to="groups" replace /> },
              { path: 'groups', element: <LocationGroupTab /> },
              { path: 'locations', element: <LocationTab /> },
              { path: 'unassigned', element: <UnassignedLocationTab /> },
            ],
          },

          { path: 'add', element: <AddLocationGroupPage /> },
          {
            path: 'publications',
            children: [
              { path: '', element: <PublicationPage /> },
              {
                path: 'products',
                element: <ProductPublicationPage />,
              },
            ],
          },
          {
            path: 'assembly-publications',
            element: <AssemblyPublicationPage />,
          },
          {
            path: 'tags',
            children: [
              { path: '', element: <ListLocationTagsPage /> },
              { path: 'add', element: <AddLocationTagPage /> },
              { path: 'edit/:tagId', element: <EditLocationTagPage /> },
            ],
          },
          {
            path: 'group/:locationGroupId',
            element: <LocationGroupSubNavbar />,
            children: [
              { path: '', element: <Navigate to="timings" replace /> },
              { path: 'details', element: <DetailLocationGroupPage /> },
              { path: 'assign', element: <AssignLocationGroupPage /> },
              { path: 'schedule', element: <ScheduleLocationGroupPage /> },
              { path: 'calendar', element: <CalendarLocationGroupPage /> },
              { path: 'timings', element: <TimingLocationGroupPage /> },
            ],
          },
          {
            path: ':locationCode',
            element: <LocationSubNavbar />,
            children: [
              { path: '', element: <Navigate to="timings" replace /> },
              { path: 'details', element: <DetailLocationPage /> },
              { path: 'schedule', element: <ScheduleLocationPage /> },
              { path: 'calendar', element: <CalendarLocationPage /> },
              { path: 'time-machine', element: <ViewLocationHistoryPage /> },
              { path: 'timings', element: <TimingLocationPage /> },
              {
                path: 'overrides/:scheduleAssemblyId',
                element: <NotFoundPage />,
              },
            ],
          },
          {
            path: 'schedule-components',
            element: <ScheduleComponentPage />,
          },
          // ! unused/unreachable route/path (LOCATIONS_SCHEDULE_CONTENT)
          {
            path: 'preview',
            element: <SchedulePreviewPage />,
          },
        ],
      },
      {
        path: 'assemblies',
        children: [
          {
            path: '',
            async lazy() {
              let { ListAssemblyPage } = await import(
                'src/features/assemblies/listAssemblyPage/ListAssemblyPage'
              );

              return {
                Component: ListAssemblyPage,
              };
            },
          },
          { path: 'layouts', element: <ListLayoutsPage /> },
          {
            path: 'layouts',
            children: [
              {
                path: '',
                element: <ListLayoutsPage />,
              },
              {
                path: 'add',
                element: <AddLayoutsPage />,
              },
              { path: 'edit/:layoutId', element: <EditLayoutsPage /> },
              {
                path: 'create/:layoutId',
                element: <CreateSectionsLayoutsPage />,
              },
              {
                path: 'create/rotation/:layoutId',
                element: <CreateRotationSectionsLayoutsPage />,
              },
            ],
          },
          { path: 'add', element: <AddAssemblyPage /> },
          { path: 'edit/:assemblyId', element: <EditAssemblyPage /> },
          { path: 'details/:assemblyId', element: <DetailAssemblyPage /> },
          {
            path: 'bulk-replace',
            element: <BulkReplaceReviewPage />,
          },
          {
            path: ':locationCode',
            children: [
              {
                path: 'overrides/:scheduleAssemblyId',
                element: <DetailAssemblyPage />,
              },
              {
                path: 'scheduleComponent/:scheduleAssemblyId',
                element: <DetailAssemblyPage />,
              },
            ],
          },
          {
            path: 'tags',
            children: [
              { path: '', element: <ManageAssemblyTagPage /> },
              { path: 'add', element: <AddAssemblyTagPage /> },
              { path: 'edit/:tagId', element: <EditAssemblyTagPage /> },
            ],
          },
        ],
      },
      {
        path: 'products',
        children: [
          {
            path: '',
            element: <ListProductsPage />,
          },
          {
            path: 'categories',
            children: [
              { path: 'add', element: <AddProductCategoryPage /> },
              {
                path: 'edit/:productCategoryId',
                element: <EditProductCategoryPage />,
              },
              {
                path: 'manage',
                element: <ManageProductCategoryPage />,
              },
            ],
          },
          {
            path: 'add',
            element: <AddProductPage />,
          },
          {
            path: 'edit/:productCode',
            children: [
              { path: '', element: <EditProductPage /> },
              {
                path: 'variations/:variationCode',
                children: [
                  {
                    path: 'template',
                    children: [
                      {
                        path: 'add',
                        element: <AddVariationTemplatePage />,
                      },
                      {
                        path: 'edit/:templateId',
                        element: <EditVariationTemplatePage />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'details/:productCode',
            element: <ReviewProductPage />,
          },
        ],
      },
      {
        path: 'settings',
        element: <SettingsSubNavbar />,
        children: [
          {
            path: '',
            element: <Navigate to="configurations" replace />,
          },
          {
            path: 'configurations',
            element: <EditGlobalSettingsPage />,
          },
          {
            path: 'accessibility',
            element: <EditAccessibilityPage />,
          },
          {
            path: 'customerTriggeredEvents',
            element: <CustomerTriggeredEventsPage />,
          },
          {
            path: 'actionTracking',
            element: <ListUserActionsPage />,
          },
          {
            path: 'about',
            element: <AboutPage />,
          },
        ],
      },
      {
        // tried http://localhost:4200/#/devtools/widgetPage, nothing there - missing impacted props anyways
        path: '/devtools',
        element: (
          <DesignSystemThemeProvider>
            <Outlet />
          </DesignSystemThemeProvider>
        ),
        children: [
          { path: 'layouts', element: <LayoutsPage /> },
          { path: 'assemblyState', element: <AssemblyState /> },
          {
            path: 'widgetPage',
            element: <FilterDropdownWithSearch onChange={() => {}} />,
          },
          { path: 'publications', element: <DevPublications /> },
          { path: 'assetUpload', element: <AssetUploadModal /> },
          { path: 'design-system', element: <DesignSystemPage /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export const routesForOwnerOperator = [
  {
    path: '/',
    element: <PageWrapper />,
    children: [
      {
        path: '',
        element: <Navigate to="locations" replace />,
      },
      {
        path: 'locations',
        children: [
          {
            path: '',
            element: (
              <DesignSystemThemeProvider>
                <LocationListPageOOV2 />
              </DesignSystemThemeProvider>
            ),
            title: 'Locations',
          },
          {
            path: 'bulk-edit-messaging',
            element: (
              <DesignSystemThemeProvider>
                <BulkEditMessagingPage />
              </DesignSystemThemeProvider>
            ),
            title: 'Bulk edit messaging',
          },
          { path: 'publications', element: <PublicationPage /> },
          {
            path: ':locationCode',
            element: <LocationSubNavbar />,
            children: [
              { path: '', element: <Navigate to="schedule" replace /> },
              {
                path: 'schedule',
                children: [{ path: '', element: <ScheduleLocationPageOO /> }],
              },
              { path: 'calendar', element: <CalendarLocationPage /> },
              {
                path: 'details',
                children: [
                  {
                    path: '',
                    element: <DetailLocationPageOO />,
                    title: 'Location',
                  },
                  {
                    path: ':channelName',
                    children: [
                      {
                        path: '',
                        element: (
                          <Navigate to="menu-board-modification" replace />
                        ),
                      },
                      {
                        path: 'menu-board-modification',
                        element: (
                          <DesignSystemThemeProvider>
                            <MenuBoardModificationPage />
                          </DesignSystemThemeProvider>
                        ),
                      },
                    ],
                  },
                ],
              },
              { path: 'timings', element: <TimingLocationPage /> },
            ],
          },
        ],
      },
      {
        path: 'assemblies',
        children: [
          { path: 'details/:assemblyId', element: <DetailAssemblyPage /> },
        ],
      },
      {
        path: 'components',
        children: [
          { path: 'details/:componentId', element: <DetailComponentPage /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];
